import React, { useMemo, useCallback } from 'react';
import { Auth0Provider, AppState as Auth0AppState, User } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { AUTH0_CONFIG } from '../../config/api.config';

interface Props {
    children: React.ReactNode;
}

interface AppState extends Auth0AppState {
    returnTo?: string;
}

const Auth0ProviderWithNavigate: React.FC<Props> = ({ children }) => {
    const navigate = useNavigate();

    // Memoize the configuration check to prevent unnecessary rerenders
    const isConfigValid = useMemo(() => {
        const hasValidConfig = !!(AUTH0_CONFIG.domain && AUTH0_CONFIG.clientId && AUTH0_CONFIG.audience);

        console.log('Auth0Provider setup', {
            hasConfig: hasValidConfig,
            domain: AUTH0_CONFIG.domain,
            audience: AUTH0_CONFIG.audience
        });

        if (!hasValidConfig) {
            console.error('Missing Auth0 configuration. Please check your environment variables.');
        }

        return hasValidConfig;
    }, []);

    const onRedirectCallback = useCallback((appState?: AppState, user?: User): void => {
        console.log('Auth0 redirect callback - DETAILED', {
            hasAppState: !!appState,
            hasUser: !!user,
            appState: appState,
            returnTo: appState?.returnTo
        });

        if (user) {
            console.log('Auth0 user authenticated successfully', {
                email: user.email,
                isEmailVerified: user.email_verified
            });
        }

        // Always redirect to root first to let the application fetch role information
        console.log('Auth0 redirect - going to root for proper role resolution');
        navigate('/', { replace: true });
    }, [navigate]);


    // Memoize the Auth0 provider configuration
    const providerConfig = useMemo(() => ({
        domain: AUTH0_CONFIG.domain,
        clientId: AUTH0_CONFIG.clientId,
        authorizationParams: {
            redirect_uri: window.location.origin,
            audience: AUTH0_CONFIG.audience,
            scope: 'openid profile email'
        },
        useRefreshTokens: true,
        cacheLocation: "localstorage" as const
    }), []);

    if (!isConfigValid) {
        return null;
    }

    return (
        <Auth0Provider
            {...providerConfig}
            onRedirectCallback={onRedirectCallback}
        >
            {children}
        </Auth0Provider>
    );
};

export default React.memo(Auth0ProviderWithNavigate);